/**
 * Product Service with Optimized GraphQL Queries and Caching
 * This service replaces the N+1 query pattern with optimized batch queries
 * and implements intelligent caching with proper invalidation
 */

import { generateClient } from 'aws-amplify/api';
import {
  listProductsWithPrimaryImages,
  listProductsWithAllImages,
  getProductWithImages,
  productsByCategoryWithImages,
  listFeaturedProductsWithImages,
  listProductsMinimal,
  listProductsForAdmin,
  searchProductsWithImages
} from '../graphql/optimizedQueries';
import { createProduct, updateProduct, deleteProduct } from '../graphql/mutations';
import graphqlCache, { 
  withCache, 
  invalidateProductCache, 
  invalidateCategoryCache,
  getCacheStats 
} from '../utils/graphqlCache';

// Staff-only mutations below state authMode: 'userPool' rather than
// inheriting whatever the default is at call time. Reads here are public, so
// the default suits them; only the writes need the signed-in token.
class ProductService {
  constructor() {
    this.client = generateClient();
    this.cachedClient = withCache(this.client.graphql.bind(this.client));
    this.imageCache = new Map(); // Additional image URL cache
  }

  /**
   * Get products with primary images optimized for listing pages
   * This replaces the N+1 query pattern with a single optimized query
   * @param {object} options - Query options
   * @returns {Promise<object>} Products with primary images
   */
  async getProductsForListing(options = {}) {
    const {
      filter = { isActive: { eq: true } },
      limit = 20,
      nextToken = null,
      useCache = true
    } = options;

    try {
      console.log('[ProductService] Fetching products for listing...');
      
      const queryMethod = useCache ? this.cachedClient : this.client.graphql.bind(this.client);
      
      const result = await queryMethod({
        query: listProductsWithPrimaryImages,
        variables: { filter, limit, nextToken }
      });

      // Process and enhance the data
      const processedProducts = this.processProductData(result.data.listProducts.items);
      
      return {
        products: processedProducts,
        nextToken: result.data.listProducts.nextToken,
        totalCount: processedProducts.length
      };
    } catch (error) {
      console.error('[ProductService] Error fetching products for listing:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get products by category with optimized image loading
   * @param {string} categoryId - Category ID
   * @param {object} options - Query options
   * @returns {Promise<object>} Category products with images
   */
  async getProductsByCategory(categoryId, options = {}) {
    const {
      filter = { isActive: { eq: true } },
      limit = 20,
      nextToken = null,
      sortDirection = 'DESC',
      useCache = true
    } = options;

    try {
      console.log(`[ProductService] Fetching products for category: ${categoryId}`);
      
      const queryMethod = useCache ? this.cachedClient : this.client.graphql.bind(this.client);
      
      const result = await queryMethod({
        query: productsByCategoryWithImages,
        variables: {
          categoryId,
          filter,
          limit,
          nextToken,
          sortDirection
        }
      });

      const processedProducts = this.processProductData(result.data.productsByCategoryId.items);
      
      return {
        products: processedProducts,
        nextToken: result.data.productsByCategoryId.nextToken,
        categoryId,
        totalCount: processedProducts.length
      };
    } catch (error) {
      console.error(`[ProductService] Error fetching products for category ${categoryId}:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Get featured products with images for homepage
   * @param {object} options - Query options
   * @returns {Promise<Array>} Featured products
   */
  async getFeaturedProducts(options = {}) {
    const {
      filter = { isActive: { eq: true } },
      limit = 6,
      useCache = true
    } = options;

    try {
      console.log('[ProductService] Fetching featured products...');
      
      const queryMethod = useCache ? this.cachedClient : this.client.graphql.bind(this.client);
      
      const result = await queryMethod({
        query: listFeaturedProductsWithImages,
        variables: { filter, limit }
      });

      const featuredProducts = result.data.listFeaturedProducts.items
        .filter(item => item.product)
        .map(item => ({
          ...item.product,
          displayOrder: item.displayOrder,
          featuredStartDate: item.startDate,
          featuredEndDate: item.endDate,
          primaryImage: this.extractPrimaryImage(item.product.images?.items || [])
        }))
        .sort((a, b) => a.displayOrder - b.displayOrder);

      return featuredProducts;
    } catch (error) {
      console.error('[ProductService] Error fetching featured products:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get single product with all details and images
   * @param {string} productId - Product ID
   * @param {boolean} useCache - Whether to use cache
   * @returns {Promise<object>} Complete product data
   */
  async getProductDetails(productId, useCache = true) {
    try {
      console.log(`[ProductService] Fetching product details: ${productId}`);
      
      const queryMethod = useCache ? this.cachedClient : this.client.graphql.bind(this.client);
      
      const result = await queryMethod({
        query: getProductWithImages,
        variables: { id: productId }
      });

      if (!result.data.getProduct) {
        throw new Error('Product not found');
      }

      const product = this.processProductData([result.data.getProduct])[0];
      
      // Add additional processing for single product view
      product.allImages = product.images || [];
      product.primaryImage = this.extractPrimaryImage(product.allImages);
      product.videos = result.data.getProduct.videos?.items || [];
      product.variants = result.data.getProduct.variants?.items || [];
      product.reviews = result.data.getProduct.reviews?.items || [];

      return product;
    } catch (error) {
      console.error(`[ProductService] Error fetching product details ${productId}:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Search products with optimized image loading
   * @param {string} searchTerm - Search term
   * @param {object} options - Search options
   * @returns {Promise<object>} Search results
   */
  async searchProducts(searchTerm, options = {}) {
    const {
      categoryId = null,
      priceRange = null,
      farmingMethod = null,
      limit = 20,
      nextToken = null,
      useCache = false // Search results typically shouldn't be cached long
    } = options;

    try {
      console.log(`[ProductService] Searching products: "${searchTerm}"`);
      
      // Build filter based on search criteria
      const filter = this.buildSearchFilter(searchTerm, {
        categoryId,
        priceRange,
        farmingMethod
      });

      const queryMethod = useCache ? this.cachedClient : this.client.graphql.bind(this.client);
      
      const result = await queryMethod({
        query: searchProductsWithImages,
        variables: { filter, limit, nextToken }
      });

      const processedProducts = this.processProductData(result.data.listProducts.items);
      
      // Client-side search scoring (could be moved to server-side)
      const scoredProducts = this.scoreSearchResults(processedProducts, searchTerm);

      return {
        products: scoredProducts,
        nextToken: result.data.listProducts.nextToken,
        searchTerm,
        totalCount: scoredProducts.length
      };
    } catch (error) {
      console.error(`[ProductService] Error searching products "${searchTerm}":`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Get minimal product data for quick loading (e.g., mobile)
   * @param {object} options - Query options
   * @returns {Promise<Array>} Minimal product data
   */
  async getProductsMinimal(options = {}) {
    const {
      filter = { isActive: { eq: true } },
      limit = 50,
      nextToken = null,
      useCache = true
    } = options;

    try {
      const queryMethod = useCache ? this.cachedClient : this.client.graphql.bind(this.client);
      
      const result = await queryMethod({
        query: listProductsMinimal,
        variables: { filter, limit, nextToken }
      });

      return {
        products: result.data.listProducts.items.map(product => ({
          ...product,
          primaryImage: this.extractPrimaryImage(product.images?.items || [])
        })),
        nextToken: result.data.listProducts.nextToken
      };
    } catch (error) {
      console.error('[ProductService] Error fetching minimal products:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get products for admin dashboard with enhanced data
   * @param {object} options - Query options
   * @returns {Promise<object>} Admin product data
   */
  async getProductsForAdmin(options = {}) {
    const {
      filter = {},
      limit = 100,
      nextToken = null,
      useCache = true
    } = options;

    try {
      const queryMethod = useCache ? this.cachedClient : this.client.graphql.bind(this.client);
      
      const result = await queryMethod({
        query: listProductsForAdmin,
        variables: { filter, limit, nextToken }
      });

      const processedProducts = this.processProductData(result.data.listProducts.items);

      return {
        products: processedProducts,
        nextToken: result.data.listProducts.nextToken,
        totalCount: processedProducts.length,
        stats: this.calculateProductStats(processedProducts)
      };
    } catch (error) {
      console.error('[ProductService] Error fetching admin products:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Process product data with image optimization
   * @param {Array} products - Raw product data
   * @returns {Array} Processed product data
   */
  processProductData(products) {
    return products.map(product => {
      const primaryImage = this.extractPrimaryImage(product.images?.items || []);
      
      return {
        ...product,
        primaryImage,
        images: product.images?.items || [],
        hasDiscount: product.discountPrice && product.discountPrice < product.price,
        discountPercentage: this.calculateDiscountPercentage(product.price, product.discountPrice),
        finalPrice: product.discountPrice || product.price,
        isInStock: product.stock > 0,
        category: product.category || null
      };
    });
  }

  /**
   * Extract primary image with fallback handling
   * @param {Array} images - Array of product images
   * @returns {object|null} Primary image object or null
   */
  extractPrimaryImage(images) {
    if (!images || images.length === 0) return null;
    
    // Find primary image
    const primary = images.find(img => img.isPrimary);
    if (primary) return primary;
    
    // Fallback to first image
    return images[0] || null;
  }

  /**
   * Build search filter for GraphQL query
   * @param {string} searchTerm - Search term
   * @param {object} criteria - Additional search criteria
   * @returns {object} GraphQL filter object
   */
  buildSearchFilter(searchTerm, criteria = {}) {
    const filter = {
      isActive: { eq: true }
    };

    // Add text search (this is simplified - in production you'd use OpenSearch)
    if (searchTerm) {
      filter.or = [
        { name: { contains: searchTerm } },
        { description: { contains: searchTerm } },
        { tags: { contains: searchTerm } }
      ];
    }

    // Add category filter
    if (criteria.categoryId) {
      filter.categoryId = { eq: criteria.categoryId };
    }

    // Add price range filter
    if (criteria.priceRange) {
      const { min, max } = criteria.priceRange;
      if (min !== undefined) filter.price = { ...filter.price, gte: min };
      if (max !== undefined) filter.price = { ...filter.price, lte: max };
    }

    // Add farming method filter
    if (criteria.farmingMethod) {
      filter.farmingMethod = { eq: criteria.farmingMethod };
    }

    return filter;
  }

  /**
   * Score search results for relevance
   * @param {Array} products - Products to score
   * @param {string} searchTerm - Search term
   * @returns {Array} Scored and sorted products
   */
  scoreSearchResults(products, searchTerm) {
    const term = searchTerm.toLowerCase();
    
    return products
      .map(product => {
        let score = 0;
        
        // Name match (highest priority)
        if (product.name.toLowerCase().includes(term)) score += 10;
        
        // Description match
        if (product.description.toLowerCase().includes(term)) score += 5;
        
        // Tags match
        if (product.tags?.some(tag => tag.toLowerCase().includes(term))) score += 3;
        
        // Exact match bonus
        if (product.name.toLowerCase() === term) score += 20;
        
        return { ...product, searchScore: score };
      })
      .filter(product => product.searchScore > 0)
      .sort((a, b) => b.searchScore - a.searchScore);
  }

  /**
   * Calculate discount percentage
   * @param {number} originalPrice - Original price
   * @param {number} discountPrice - Discounted price
   * @returns {number} Discount percentage
   */
  calculateDiscountPercentage(originalPrice, discountPrice) {
    if (!discountPrice || discountPrice >= originalPrice) return 0;
    return Math.round(((originalPrice - discountPrice) / originalPrice) * 100);
  }

  /**
   * Calculate product statistics for admin dashboard
   * @param {Array} products - Products array
   * @returns {object} Statistics
   */
  calculateProductStats(products) {
    return {
      total: products.length,
      active: products.filter(p => p.isActive).length,
      inStock: products.filter(p => p.stock > 0).length,
      outOfStock: products.filter(p => p.stock === 0).length,
      featured: products.filter(p => p.isFeatured).length,
      withDiscount: products.filter(p => p.hasDiscount).length
    };
  }

  /**
   * Invalidate cache when product data changes
   * @param {string} productId - Product that was updated
   * @param {string} categoryId - Category that was updated
   */
  invalidateCache(productId = null, categoryId = null) {
    if (productId) {
      invalidateProductCache(productId);
    }
    if (categoryId) {
      invalidateCategoryCache(categoryId);
    }
    
    console.log('[ProductService] Cache invalidated for product/category changes');
  }

  /**
   * Get cache statistics
   * @returns {object} Cache statistics
   */
  getCacheStats() {
    return getCacheStats();
  }

  /**
   * COMPATIBILITY METHOD: Fetch products with primary images (for existing code)
   * This method maintains compatibility with existing ProductsPage.js code
   */
  async fetchProductsWithPrimaryImages(filter = {}, limit = 100, nextToken = null, forceRefresh = false) {
    try {
      console.log('🚀 [OPTIMIZED] Fetching products with primary images in SINGLE API call...');
      
      const queryMethod = !forceRefresh ? this.cachedClient : this.client.graphql.bind(this.client);
      
      const query = /* GraphQL */ `
        query ListProductsWithPrimaryImages(
          $filter: ModelProductFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
            items {
              id
              name
              description
              shortDescription
              categoryId
              category {
                id
                name
                __typename
              }
              price
              discountPrice
              stock
              sku
              isActive
              isFeatured
              tags
              farmingMethod
              harvestDate
              expiryDate
              images {
                items {
                  id
                  url
                  altText
                  isPrimary
                  order
                  __typename
                }
                __typename
              }
              createdAt
              updatedAt
              __typename
            }
            nextToken
            __typename
          }
        }
      `;

      const result = await queryMethod({
        query,
        variables: { filter, limit, nextToken }
      });

      console.log(`✅ SUCCESS: Got ${result.data.listProducts.items.length} products + images in 1 API call!`);

      return result.data.listProducts;
    } catch (error) {
      console.error('❌ Error in fetchProductsWithPrimaryImages:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Clean product data to match GraphQL input types
   * @param {object} productData - Raw product data
   * @returns {object} Cleaned product data
   */
  cleanProductData(productData) {
    // Fields allowed in CreateProductInput/UpdateProductInput
    const allowedFields = [
      'name', 'description', 'shortDescription', 'categoryId', 'price', 
      'discountPrice', 'stock', 'sku', 'weight', 'dimensions', 'isActive', 
      'isFeatured', 'tags', 'nutritionalInfo', 'farmingMethod', 'harvestDate', 'expiryDate'
    ];
    
    const cleanedData = {};
    allowedFields.forEach(field => {
      if (productData[field] !== undefined && productData[field] !== '') {
        let value = productData[field];
        
        // Convert numeric string values to proper types
        if (field === 'price' || field === 'discountPrice' || field === 'weight') {
          if (value === '' || value === null) {
            // Don't include empty numeric fields
            return;
          }
          value = parseFloat(value);
          if (isNaN(value)) {
            // Skip invalid numeric values
            return;
          }
        } else if (field === 'stock') {
          if (value === '' || value === null) {
            // Don't include empty stock
            return;
          }
          value = parseInt(value);
          if (isNaN(value)) {
            // Skip invalid integer values
            return;
          }
        }
        
        cleanedData[field] = value;
      }
    });
    
    return cleanedData;
  }

  /**
   * Create a new product
   * @param {object} productData - Product data to create
   * @returns {Promise<object>} Created product
   */
  async createProduct(productData) {
    try {
      console.log('[ProductService] Creating product:', productData);
      
      const cleanedData = this.cleanProductData(productData);
      console.log('[ProductService] Cleaned product data:', cleanedData);
      
      const result = await this.client.graphql({
        query: createProduct,
        authMode: 'userPool',
        variables: {
          input: cleanedData
        }
      });

      // Invalidate cache after creation
      this.invalidateCache(null, productData.categoryId);
      
      return result.data.createProduct;
    } catch (error) {
      console.error('[ProductService] Error creating product:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update an existing product
   * @param {string} productId - Product ID to update
   * @param {object} productData - Updated product data
   * @returns {Promise<object>} Updated product
   */
  async updateProduct(productId, productData) {
    try {
      console.log('[ProductService] Updating product:', productId, productData);
      
      const cleanedData = this.cleanProductData(productData);
      console.log('[ProductService] Cleaned update data:', cleanedData);
      
      // Custom update mutation that excludes problematic fields like favorites
      const customUpdateProduct = /* GraphQL */ `
        mutation UpdateProduct(
          $input: UpdateProductInput!
          $condition: ModelProductConditionInput
        ) {
          updateProduct(input: $input, condition: $condition) {
            id
            name
            description
            shortDescription
            categoryId
            category {
              id
              name
              __typename
            }
            price
            discountPrice
            stock
            sku
            weight
            dimensions
            isActive
            isFeatured
            tags
            nutritionalInfo
            farmingMethod
            harvestDate
            expiryDate
            createdAt
            updatedAt
            __typename
          }
        }
      `;
      
      const result = await this.client.graphql({
        query: customUpdateProduct,
        authMode: 'userPool',
        variables: {
          input: {
            id: productId,
            ...cleanedData
          }
        }
      });

      // Invalidate cache after update
      this.invalidateCache(productId, productData.categoryId);
      
      return result.data.updateProduct;
    } catch (error) {
      console.error('[ProductService] Error updating product:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete a product
   * @param {string} productId - Product ID to delete
   * @returns {Promise<object>} Deleted product
   */
  async deleteProduct(productId) {
    try {
      console.log('[ProductService] Deleting product:', productId);
      
      const result = await this.client.graphql({
        query: deleteProduct,
        authMode: 'userPool',
        variables: {
          input: { id: productId }
        }
      });

      // Invalidate cache after deletion
      this.invalidateCache(productId);
      
      return result.data.deleteProduct;
    } catch (error) {
      console.error('[ProductService] Error deleting product:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle and format errors
   * @param {Error} error - Original error
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.errors) {
      // GraphQL errors
      const message = error.errors.map(e => e.message).join(', ');
      return new Error(`GraphQL Error: ${message}`);
    }
    
    return error;
  }
}

// Create singleton instance
const productServiceInstance = new ProductService();

// Add static methods to the class for backwards compatibility
ProductService.fetchProductsWithPrimaryImages = (...args) => productServiceInstance.fetchProductsWithPrimaryImages(...args);
ProductService.fetchProduct = (...args) => productServiceInstance.getProductDetails(...args);
ProductService.getProductsForListing = (...args) => productServiceInstance.getProductsForListing(...args);
ProductService.getProductsByCategory = (...args) => productServiceInstance.getProductsByCategory(...args);
ProductService.getFeaturedProducts = (...args) => productServiceInstance.getFeaturedProducts(...args);
ProductService.getProductDetails = (...args) => productServiceInstance.getProductDetails(...args);
ProductService.searchProducts = (...args) => productServiceInstance.searchProducts(...args);
ProductService.createProduct = (...args) => productServiceInstance.createProduct(...args);
ProductService.updateProduct = (...args) => productServiceInstance.updateProduct(...args);
ProductService.deleteProduct = (...args) => productServiceInstance.deleteProduct(...args);
ProductService.invalidateCache = (...args) => productServiceInstance.invalidateCache(...args);
ProductService.getCacheStats = (...args) => productServiceInstance.getCacheStats(...args);

// Export the class for those who need it
export { ProductService };

// Export singleton instance as default
export default productServiceInstance;